import { useEffect, useMemo, useState } from 'react';
import { DIMENSION_LABELS, type DimensionCode } from '@cessi/shared';
import { etiquetaDimensionLineas } from '../lib/dimLabels';
import {
  axisAngleDeg,
  polarToCartesian,
  radarPolygonFromLevels,
  ringPolygon,
} from '../lib/radar';

const AXES: Exclude<DimensionCode, 'IN'>[] = ['EV', 'PC', 'OD', 'DC', 'GS'];

/** Respeta la preferencia del sistema; el motion es decorativo, nunca informativo. */
function usePrefiereMenosMovimiento(): boolean {
  const [reducir, setReducir] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const alCambiar = () => setReducir(mq.matches);
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, []);

  return reducir;
}

/** Progreso 0→1 con ease-out, o 1 directo si no hay animación. */
function useEntrada(activo: boolean, semilla: string, duracion = 900): number {
  const [t, setT] = useState(activo ? 0 : 1);

  useEffect(() => {
    if (!activo) {
      setT(1);
      return;
    }
    setT(0);
    let raf = 0;
    const inicio = performance.now();
    const tick = (ahora: number) => {
      const p = Math.min(1, (ahora - inicio) / duracion);
      setT(1 - (1 - p) ** 3);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [activo, semilla, duracion]);

  return t;
}

function anclaTexto(x: number, cx: number): 'start' | 'middle' | 'end' {
  if (Math.abs(x - cx) < 6) return 'middle';
  return x > cx ? 'start' : 'end';
}

type Props = {
  niveles: Record<(typeof AXES)[number], number>;
  /** Si true, anima desde 0 al valor. */
  animar?: boolean;
  size?: number;
  /** Dimensión foco: se resalta la etiqueta del eje. */
  foco?: DimensionCode;
  /** Oculta valores N junto a cada eje (uso decorativo). */
  compacto?: boolean;
};

export function RadarChart({ niveles, animar = true, size = 340, foco, compacto = false }: Props) {
  const reducir = usePrefiereMenosMovimiento();
  const semilla = AXES.map((a) => niveles[a] ?? 1).join('-');
  const t = useEntrada(animar && !reducir, semilla);

  const cx = size / 2;
  const cy = size / 2;
  // Radio más corto para dejar margen a nombres completos en dos líneas.
  const rMax = size * 0.26;
  const pad = size * 0.04;

  const levels = useMemo(() => AXES.map((a) => (niveles[a] ?? 1) * t), [semilla, t]);

  const escala = Array.from({ length: 8 }, (_, i) => (i + 1) * 0.5);
  const rings = escala.map((n) => ringPolygon(cx, cy, rMax * (n / 4), 5));
  const dataPoly = radarPolygonFromLevels(levels, cx, cy, rMax);
  const vertices = levels.map((lvl, i) =>
    polarToCartesian(cx, cy, (Math.max(0, Math.min(4, lvl)) / 4) * rMax, axisAngleDeg(i, 5)),
  );
  const labels = AXES.map((code, i) => {
    const punta = polarToCartesian(cx, cy, rMax + size * 0.13, axisAngleDeg(i, 5));
    return { code, ...punta, nivel: niveles[code] ?? 1 };
  });

  const resumen = AXES.map((a) => `${DIMENSION_LABELS[a]}: nivel ${niveles[a] ?? 1} de 4`).join('; ');

  return (
    <figure className="radar-figure">
      <svg
        className="radar"
        width={size}
        height={size}
        viewBox={`${-pad} ${-pad} ${size + pad * 2} ${size + pad * 2}`}
        role="img"
        aria-label={`Radar de madurez por dimensión, escala de 0 a 4 en pasos de medio punto. ${resumen}.`}
        overflow="visible"
      >
        {rings.map((pts, i) => (
          <polygon key={i} points={pts} className="radar__ring" />
        ))}

        {AXES.map((_, i) => {
          const punta = polarToCartesian(cx, cy, rMax, axisAngleDeg(i, 5));
          return <line key={i} x1={cx} y1={cy} x2={punta.x} y2={punta.y} className="radar__axis" />;
        })}

        {!compacto &&
          [0, ...escala].map((n) => (
            <text
              key={n}
              x={cx + 6}
              y={cy - (rMax * n) / 4}
              className="radar__nivel"
              dominantBaseline="middle"
              fontSize={9}
              fontWeight={600}
              fill="var(--co-texto-debil)"
            >
              {Number.isInteger(n) ? n : n.toFixed(1)}
            </text>
          ))}

        <polygon
          points={dataPoly}
          className="radar__area"
          fill="var(--ma-amarillo)"
          fillOpacity={0.72}
          stroke="var(--ma-negro)"
          strokeWidth={2}
          strokeLinejoin="round"
        />

        {vertices.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={4}
            className="radar__punto"
            fill="var(--co-superficie)"
            stroke="var(--ma-negro)"
            strokeWidth={2}
          />
        ))}

        {!compacto &&
          labels.map((l) => {
            const [linea1, linea2] = etiquetaDimensionLineas(l.code);
            const ancla = anclaTexto(l.x, cx);
            return (
              <g key={l.code}>
                <text
                  x={l.x}
                  y={l.y - 10}
                  textAnchor={ancla}
                  dominantBaseline="middle"
                  className={`radar__label${foco === l.code ? ' radar__label--foco' : ''}`}
                  fill={foco === l.code ? 'var(--ma-negro)' : undefined}
                >
                  <tspan x={l.x} dy="0">
                    {linea1}
                  </tspan>
                  <tspan x={l.x} dy="11">
                    {linea2}
                  </tspan>
                </text>
                <text
                  x={l.x}
                  y={l.y + 16}
                  textAnchor={ancla}
                  dominantBaseline="middle"
                  className="radar__valor"
                  fontSize={10}
                  fontWeight={700}
                  fill="var(--ma-negro)"
                >
                  N{l.nivel}
                </text>
              </g>
            );
          })}
      </svg>
      {!compacto && (
        <figcaption className="radar__leyenda">
          {AXES.map((code) => (
            <span key={code}>
              {DIMENSION_LABELS[code]}
              <em> N{niveles[code] ?? 1}</em>
            </span>
          ))}
        </figcaption>
      )}
    </figure>
  );
}

/** Ancla visual atmosférica de la landing: decorativa, nunca informativa. */
export function RadarPreview({ size = 380 }: { size?: number }) {
  return (
    <div className="radar-preview" aria-hidden="true">
      <RadarChart
        size={size}
        animar
        compacto
        niveles={{ EV: 3, PC: 2.2, OD: 2.8, DC: 2, GS: 2.5 }}
      />
    </div>
  );
}

const DIAL_MIN = 0;
const DIAL_MAX = 4;
const DIAL_INICIO = 180;
const DIAL_FIN = 360;

function arco(cx: number, cy: number, r: number, desdeDeg: number, hastaDeg: number): string {
  const inicio = polarToCartesian(cx, cy, r, desdeDeg);
  const fin = polarToCartesian(cx, cy, r, hastaDeg);
  const largo = hastaDeg - desdeDeg > 180 ? 1 : 0;
  return `M ${inicio.x.toFixed(2)} ${inicio.y.toFixed(2)} A ${r} ${r} 0 ${largo} 1 ${fin.x.toFixed(2)} ${fin.y.toFixed(2)}`;
}

/** Dial semicircular del Índice de Innovación (0–4). */
export function IndiceDial({ valor, animar = true }: { valor: number; animar?: boolean }) {
  const reducir = usePrefiereMenosMovimiento();
  const t = useEntrada(animar && !reducir, String(valor), 800);

  const acotado = Math.max(DIAL_MIN, Math.min(DIAL_MAX, valor));
  const fraccion = ((acotado - DIAL_MIN) / (DIAL_MAX - DIAL_MIN)) * t;

  const ancho = 176;
  const alto = 104;
  const cx = ancho / 2;
  const cy = alto - 14;
  const r = 70;
  const anguloValor = DIAL_INICIO + (DIAL_FIN - DIAL_INICIO) * fraccion;

  return (
    <figure className="dial-in">
      <figcaption className="dial-in__meta">
        <span className="dial-in__label">{DIMENSION_LABELS.IN}</span>
      </figcaption>

      <p className="dial-in__valor">{valor.toFixed(1)}</p>

      <div
        className="dial-in__medidor"
        role="meter"
        aria-valuenow={Number(acotado.toFixed(1))}
        aria-valuemin={DIAL_MIN}
        aria-valuemax={DIAL_MAX}
        aria-valuetext={`Índice de innovación ${valor.toFixed(1)} sobre 4`}
      >
        <svg
          className="dial-in__svg"
          viewBox={`0 0 ${ancho} ${alto}`}
          width="100%"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d={arco(cx, cy, r, DIAL_INICIO, DIAL_FIN)}
            className="dial-in__pista"
            fill="none"
            stroke="var(--co-superficie-hundida)"
            strokeWidth={12}
            strokeLinecap="round"
          />
          <path
            d={arco(cx, cy, r, DIAL_INICIO, DIAL_FIN)}
            fill="none"
            stroke="var(--co-borde)"
            strokeWidth={1}
            strokeDasharray="2 4"
          />

          {fraccion > 0.005 && (
            <g className="dial-in__arco">
              {/* Contorno tinta + relleno amarillo: el amarillo solo no llega a 3:1 sobre blanco. */}
              <path
                d={arco(cx, cy, r, DIAL_INICIO, anguloValor)}
                fill="none"
                stroke="var(--ma-negro)"
                strokeWidth={12}
                strokeLinecap="round"
              />
              <path
                d={arco(cx, cy, r, DIAL_INICIO, anguloValor)}
                fill="none"
                stroke="var(--ma-amarillo)"
                strokeWidth={8}
                strokeLinecap="round"
              />
            </g>
          )}

          {[0, 1, 2, 3, 4].map((n) => {
            const ang = DIAL_INICIO + ((DIAL_FIN - DIAL_INICIO) * n) / 4;
            const marca = polarToCartesian(cx, cy, r + 13, ang);
            return (
              <text
                key={n}
                x={marca.x}
                y={marca.y}
                className="dial-in__tick"
                textAnchor={anclaTexto(marca.x, cx)}
                dominantBaseline="middle"
                fontSize={9}
                fontWeight={600}
                fill="var(--co-texto-debil)"
              >
                {n}
              </text>
            );
          })}
        </svg>
      </div>

      <p className="dial-in__ayuda">Índice de innovación, escala 0 a 4</p>
    </figure>
  );
}
